import { useT } from '@/session/useSession'
import styles from './Diff.module.css'

export type Change = {
  field: string
  before: string | null
  after: string | null
  before_label?: string | null
  after_label?: string | null
}

export type EnumScopes = Record<string, string>

const structured = (value: string | null) =>
  value !== null && (value.startsWith('{') || value.startsWith('['))

function Value({
  value,
  label,
  scope,
  blank,
  tone,
}: {
  value: string | null
  label?: string | null
  scope?: string
  blank: string
  tone: string
}) {
  const t = useT()
  const empty = (label ?? null) === null && (value === null || value === '')
  const named = scope !== undefined && value !== null && value !== ''
  const spelled = named ? t(`${scope}.${value}`) : null

  if ((label ?? null) === null && structured(value)) {
    return <code className={`${tone} ${styles.structured}`}>{value}</code>
  }

  return (
    <span className={empty ? `${tone} ${styles.blank}` : tone}>
      {label ?? spelled ?? (empty ? blank : value)}
    </span>
  )
}

export function Diff({
  changes,
  scope = 'audit.field',
  enums,
  action = 'update',
}: {
  changes: Change[]
  scope?: string
  enums?: EnumScopes
  action?: 'insert' | 'update' | 'delete'
}) {
  const t = useT()
  const blank = t('audit.blank')
  const stated = action === 'delete' ? 'before' : 'after'

  return (
    <dl className={styles.diff}>
      {changes.map((change) => (
        <div className={styles.change} key={change.field}>
          <dt className={styles.field}>{t(`${scope}.${change.field}`)}</dt>
          <dd className={styles.values}>
            {action === 'update' ? (
              <>
                <Value
                  value={change.before}
                  label={change.before_label}
                  scope={enums?.[change.field]}
                  blank={blank}
                  tone={styles.before}
                />
                <span aria-hidden="true">→</span>
                <Value
                  value={change.after}
                  label={change.after_label}
                  scope={enums?.[change.field]}
                  blank={blank}
                  tone={styles.after}
                />
              </>
            ) : (
              <Value
                value={change[stated]}
                label={change[`${stated}_label`]}
                scope={enums?.[change.field]}
                blank={blank}
                tone={styles.stated}
              />
            )}
          </dd>
        </div>
      ))}
    </dl>
  )
}
